import { useCallback } from 'react';
import type { WizardFlushBaselines } from './utils/flushBaselines';

type AnchorBaselineKey = 'departments' | 'users' | 'systems' | 'processes';

/**
 * The one change handler behind all four flat anchor steps: the first change
 * on an untouched done step freezes the seed baseline — also when the change
 * is "Liste leeren" emptying the table (next = []), which turns the empty
 * draft into a pending delete-all (isClearedAnchorDraft) instead of the
 * untouched seed fallback. A baseline a silent flush already moved is never
 * overwritten.
 */
export function useAnchorDraftChangeHandler<K extends AnchorBaselineKey>(config: {
  baselineKey: K;
  isDone: boolean;
  drafts: NonNullable<WizardFlushBaselines[K]>;
  baselines: WizardFlushBaselines;
  seeds: NonNullable<WizardFlushBaselines[K]>;
  setRows: React.Dispatch<React.SetStateAction<NonNullable<WizardFlushBaselines[K]>>>;
  setBaselines: React.Dispatch<React.SetStateAction<WizardFlushBaselines>>;
  notifyChange: () => void;
}) {
  const { baselineKey, isDone, drafts, seeds, setRows, setBaselines, notifyChange } = config;
  const baseline = config.baselines[baselineKey];
  const untouched = drafts.length === 0;
  return useCallback(
    (next: NonNullable<WizardFlushBaselines[K]>) => {
      if (isDone && untouched && !baseline) {
        setBaselines((prev) => {
          const frozen = { ...prev };
          frozen[baselineKey] = seeds;
          return frozen;
        });
      }
      setRows(next);
      notifyChange();
    },
    [baseline, baselineKey, isDone, notifyChange, seeds, setBaselines, setRows, untouched]
  );
}
